import { Suspense, type FC } from 'react';
import { Outlet } from 'react-router-dom';

import { useIsPlatformAdmin } from '@maestra/core/hooks/useIsPlatformAdmin';

import { useCasca } from '../../logica/casca/useCasca';
import { useMenuDaConta, type EstadoDoPlano } from '../../logica/casca/useMenuDaConta';
import { useMenuRecolhido } from '../../logica/casca/useMenuRecolhido';
import { useNotificacoes } from '../../logica/notificacoes/useNotificacoes';
import { NytaFlutuante } from './NytaFlutuante';
import { AvisoDoTitularProvider } from './AvisoDoTitular';
import { CascaVista, type PropsDaCasca } from './CascaVista';
import { modulosDoPerfil } from './modulos';
import type { PlanoDaNavegacao } from './NavegacaoDoPerfil';
import { ConteudoDasNotificacoes } from './PainelDeNotificacoes';
import { Player } from './Player';
import { CarregandoPagina } from '../padroes/Carregando';

// A CASCA LIGADA: a lógica (src/logica/casca) de um lado, o desenho (CascaVista) do outro.

const DO_PLANO: Record<Exclude<EstadoDoPlano, null>, { rotulo: string; tipo: PlanoDaNavegacao['tipo'] }> = {
  pro: { rotulo: 'Pro', tipo: 'blue' },
  pendente: { rotulo: 'Pagamento em confirmação', tipo: 'warm-gray' },
  free: { rotulo: 'Gratuito', tipo: 'gray' },
};

/** O sino aberto: a lista (e o canal ao vivo dela) só existe enquanto o painel está aberto. */
const NotificacoesDoSino: FC<{ fechar: () => void; verTodas: () => void }> = ({ fechar, verTodas }) => (
  <ConteudoDasNotificacoes n={useNotificacoes('painel')} verTodas={verTodas} aoAbrir={fechar} />
);

export const Casca: FC = () => {
  const c = useCasca();
  const conta = useMenuDaConta();
  const menu = useMenuRecolhido();
  const admin = useIsPlatformAdmin();

  // O plano é da conta; quem é da equipe de um perfil não assina por ele, e não vê o bloco.
  const plano: PlanoDaNavegacao | null = conta.plano && c.perfilAtual && !c.perfilAtual.membro ? {
    ...DO_PLANO[conta.plano],
    espaco: c.espaco,
    verPlano: () => conta.abrir('/planos'),
    assinar: conta.plano === 'free' ? () => conta.abrir('/planos') : undefined,
  } : null;

  const props: PropsDaCasca = {
    rota: c.rota,
    abrirPerfis: c.abrirPerfis,
    busca: c.busca,
    nyta: c.abrirNyta ? { aberta: c.nytaAberta, abrir: c.abrirNyta, fechar: c.fecharNyta } : undefined,
    naoLidas: c.naoLidas,
    notificacoes: (fechar) => <NotificacoesDoSino fechar={fechar} verTodas={c.verTodasAsNotificacoes} />,
    perfis: c.perfis,
    perfilAtual: c.perfilAtual?.id,
    abrirPerfil: c.abrirPerfil,
    criarPerfil: c.criarPerfil,
    navegacao: c.comNavegacao
      ? {
          rotulo: 'Módulos do perfil',
          modulos: modulosDoPerfil(admin),
          ativo: c.moduloAtivo,
          abrir: c.abrirModulo,
          perfil: c.perfilAtual,
          plano,
        }
      : undefined,
    menu,
    conta: { ...conta, sair: () => { void conta.sair(); } },
    player: c.player && (
      <Player
        faixas={c.player.faixas}
        atual={c.player.atual}
        trocar={c.player.trocar}
        abrirFaixa={c.player.abrirFaixa}
        abrirVisualizacao={c.player.abrirVisualizacao}
        fechar={c.player.fechar}
      />
    ),
  };

  return (
    <AvisoDoTitularProvider>
      <CascaVista {...props}>
        {/* Trocar de tela dentro do app: a casca fica, e só o conteúdo espera (o esqueleto). */}
        <Suspense fallback={<CarregandoPagina />}>
          <Outlet />
        </Suspense>
      </CascaVista>
      <NytaFlutuante />
    </AvisoDoTitularProvider>
  );
};

export default Casca;
